import { Box, Card, CardContent, Typography } from "@mui/material";
import dayjs from "dayjs";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { formatKpiValue } from "../../utils/prediction-utils";

type ChartCardProps = {
    title: string;
    kpi: string;
    dataKey: string;
    color: string;
    gradientId: string;
    data: any[];
    leftLabel?: string;
    rightLabel?: string;
    mode: "weekly" | "monthly" | "yearly" | "dailyInMonth" | "custom";
};

const normalizeData = (
    data: any[],
    mode: "weekly" | "monthly" | "yearly" | "dailyInMonth" | "custom"
) => {
    const safeDate = (input: any) => {
        if (!input) return null;
        const d = dayjs(input);
        return d.isValid() ? d : null;
    };

    return data.map((d) => {
        let category = "";
        let dateForTooltip = null;

        if (mode === "weekly") {
            category = d.day?.slice(0, 3) ?? "";
            dateForTooltip = d.day_date ?? null;
        } else if (mode === "monthly") {
            const start = safeDate(d.week_start_date)?.format("MMM D");
            const end = safeDate(d.week_end_date)?.format("MMM D");
            category = start && end ? (start === end ? start : `${start} - ${end}`) : "N/A";
            dateForTooltip = d.week_start_date ?? null;
        } else if (mode === "dailyInMonth" || mode === "custom") {
            const raw = d.day ?? d.day_date ?? d.date;
            category = safeDate(raw)?.format("MMM D") ?? "N/A";
            dateForTooltip = raw ?? null;
        } else if (mode === "yearly") {
            category = d.month_name?.trim().slice(0, 3) ?? "";
            dateForTooltip = d.month_date ?? null;
        }

        const dateIso = safeDate(dateForTooltip) ? safeDate(dateForTooltip)!.format("YYYY-MM-DD") : null;

        const lastYearADR = d.adr_actual / (1 + (d.adr_change || 0) / 100);
        const lastYearOccupancy = d.occupancy_actual / (1 + (d.occupancy_change || 0) / 100);
        const lastYearRevpar = d.revpar_actual / (1 + (d.revpar_change || 0) / 100);

        return {
            category,
            occupancy_actual: +d.occupancy_actual,
            occupancy_change: +d.occupancy_change,
            adr_actual: +d.adr_actual,
            adr_change: +d.adr_change,
            revpar_actual: +d.revpar_actual,
            revpar_change: +d.revpar_change,
            lastYearADR,
            lastYearOccupancy,
            lastYearRevpar,
            date: dateForTooltip,
            dateIso, 
        };
    });
};


export const ChartCard = ({
    title,
    kpi, 
    dataKey,
    color,
    gradientId,
    data,
    leftLabel,
    rightLabel,
    mode,
}: ChartCardProps) => {
    const normalized = normalizeData(data, mode);
    const categoryCount = normalized.length;
    const chartHeight = Math.min(500, 250 + categoryCount * 10);

    const renderTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const item = payload[0].payload;
            const formattedDate = item.date ? dayjs(item.date).format("MMM D, YYYY") : "N/A";

            const lastYearKeyMap: any = {
                adr_actual: "lastYearADR",
                occupancy_actual: "lastYearOccupancy",
                revpar_actual: "lastYearRevpar",
            };

            const changeKeyMap: any = {
                adr_actual: "adr_change",
                occupancy_actual: "occupancy_change",
                revpar_actual: "revpar_change",
            };

            const lastYearKey = lastYearKeyMap[dataKey];
            const changeKey = changeKeyMap[dataKey];
            const changeValue = item[changeKey] ?? null;
            const changeColor = changeValue > 0 ? "green" : changeValue < 0 ? "red" : "gray";

            return (
                <Box sx={{ p: 1, bgcolor: "white", borderRadius: 1, boxShadow: 5, zIndex: 1000 }}>
                    <Typography variant="body2">
                        <strong>{item.category}</strong> ({formattedDate})
                    </Typography>
                    <Typography variant="body2">
                        {title}: {item[dataKey] ? formatKpiValue(item[dataKey], kpi) : "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Last Year:{" "}
                        {item[lastYearKey] ? formatKpiValue(item[lastYearKey], kpi) : "N/A"}
                    </Typography>
                    {changeValue !== null && (
                        <Typography
                            variant="body2"
                            sx={{ color: changeColor, fontWeight: 500 }}
                        >
                            Change: {changeValue > 0 ? "+" : ""}
                            {changeValue.toFixed(2)}%
                        </Typography>
                    )}
                </Box>
            );
        }
        return null;
    };

    const lastYearKeyMap: any = {
        adr_actual: "lastYearADR",
        occupancy_actual: "lastYearOccupancy",
        revpar_actual: "lastYearRevpar",
    };

    const lastYearKey = lastYearKeyMap[dataKey];

    return (
        <Card
            sx={{
                borderRadius: "12px",
                boxShadow: 1,
                "&:focus": { outline: "none" },
                width: "100%",
            }}
            tabIndex={0}
        >
            <CardContent>
                <Typography
                    variant="subtitle2"
                    fontWeight={"bold"}
                    fontSize={18}
                    gutterBottom
                    mb={2}
                    mt={2}
                    sx={{ marginLeft: "33px" }}
                >
                    {title}
                </Typography>

                <Box sx={{ "& svg": { outline: "none" }, width: "100%", height: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {mode === "dailyInMonth" || mode === "custom" ? (
                            <LineChart
                                data={normalized}
                                margin={{ top: 10, right: 20, left: 20, bottom: 40 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                    dataKey="dateIso"            
                                    interval={0}
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(val: string, index: number) => {
                                        const d = dayjs(val);
                                        if (!d.isValid()) return "";

                                        if (mode === "dailyInMonth") {
                                            const dayOfMonth = d.date();
                                            const isEvery5th = dayOfMonth % 5 === 0;
                                            const isFirst = index === 0;
                                            const isLast = index === normalized.length - 1;
                                            return isEvery5th || isFirst || isLast ? d.format("MM/DD") : "";
                                        }
                                        return d.format("MM/DD");
                                    }}
                                    angle={-25}
                                    textAnchor="end"
                                />                                
                                <YAxis 
                                    // tickFormatter={(val) => formatKpiValue(val, kpi)}
                                />
                                <Tooltip cursor={false} content={renderTooltip} />
                                <Line
                                    type="monotone"
                                    dataKey={dataKey}
                                    stroke={color}
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    isAnimationActive={false}
                                    name="This Year"
                                />
                                <Line
                                    type="monotone"
                                    dataKey={lastYearKey}
                                    stroke="#d4064f"
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    isAnimationActive={false}
                                    name="Last Year"
                                />
                            </LineChart>
                        ) : (
                            <BarChart
                                data={normalized}
                                barCategoryGap={categoryCount > 12 ? "20%" : "35%"}
                                barGap={3}
                                margin={{ top: 10, right: 20, left: 20, bottom: 40 }}
                            >
                                <defs>
                                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={color} stopOpacity={0.9} />
                                        <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                    dataKey="category"
                                    interval={0}
                                    tick={{ fontSize: 11 }}
                                    angle={-25}
                                    textAnchor="end"
                                />
                                <YAxis 
                                    // tickFormatter={(val) => formatKpiValue(val, kpi)}
                                />
                                <Tooltip cursor={false} content={renderTooltip} />
                                <Bar
                                    dataKey={dataKey}
                                    fill={`url(#${gradientId})`}
                                    radius={[15, 15, 15, 15]}
                                    isAnimationActive={false}
                                    name="This Year"
                                    maxBarSize={50} 
                                />
                                <Line
                                    type="monotone"
                                    dataKey={lastYearKey}
                                    stroke="#d4064f"
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                    isAnimationActive={false}
                                    name="Last Year"
                                />
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 3, mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: color, borderRadius: 1.5 }} />
                        <Typography variant="body2" sx={{ fontSize: 12 }}>This Year</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 12, height: 2, bgcolor: "#d4064f" }} />
                        <Typography variant="body2" sx={{ fontSize: 12 }}>Last Year</Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5, px: 2, ml: 3 }}>
                    <Typography variant="body2" sx={{ fontSize: 12 }}>{leftLabel}</Typography>
                    <Typography variant="body2" sx={{ fontSize: 12 }}>{rightLabel}</Typography>
                </Box>
            </CardContent>
        </Card>
    );
};